// Thigh (Quadriceps) Template Configuration
// Questions simplified to 12-year-old reading level

export const thighTemplate = {
  id: 'THIGH',
  name: 'Thigh (Quadriceps)',
  icon: '🦵',
  dominantSide: 'leg',

  fields: {
    injuredSide: {
      type: 'radio',
      label: 'Which leg has the thigh problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    injuryDate: {
      type: 'date',
      label: 'When did your thigh problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly during activity', 'After being hit', 'Slowly over time'],
      required: true
    },

    injuryType: {
      type: 'radio',
      label: 'Was there direct contact?',
      options: ['Direct contact', 'Non-contact'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Front thigh pain',
        'Sudden pain while sprinting or kicking',
        'Big bruise or swelling',
        'Hard lump',
        'Tightness or can\'t bend knee',
        'Weakness',
        'Popping or tearing feeling',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    injuryDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your thigh problem started',
      rows: 3,
      required: true
    },

    // Mechanism Section
    activityWhenInjured: {
      type: 'text',
      label: 'What were you doing when it happened?',
      placeholder: 'e.g., sprinting, kicking a ball, tackling'
    },

    directBlow: {
      type: 'radio',
      label: 'Were you hit directly on the thigh (e.g., a knee or boot)?',
      options: ['Yes', 'No']
    },

    feltPop: {
      type: 'radio',
      label: 'Did you feel or hear a "pop" or tearing feeling?',
      options: ['Yes', 'No']
    },

    immediatelyStopped: {
      type: 'radio',
      label: 'Did you have to stop what you were doing straight away?',
      options: ['Yes', 'No']
    },

    couldKneelOnIt: {
      type: 'radio',
      label: 'Could you kneel on the injured leg afterwards?',
      options: ['Yes', 'No']
    },

    kneeFlexionRange: {
      type: 'radio',
      label: 'How far can you bend your knee on the injured side?',
      options: [
        'Full bend (heel touches bottom)',
        'More than 90 degrees (past a right angle)',
        'Less than 90 degrees (can\'t reach a right angle)',
        'Hardly at all'
      ]
    },

    // Pain Section
    painLocation: {
      type: 'radio',
      label: 'Where is the pain in your thigh?',
      options: [
        'Upper near hip',
        'Middle front',
        'Lower near knee',
        'Wraps around side'
      ]
    },

    painCharacter: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Sharp', 'Aching', 'Throbbing', 'Cramping', 'Other']
    },

    painCharacterOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painCharacter', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painWithBending: {
      type: 'radio',
      label: 'Does it hurt when you bend your knee?',
      options: ['Yes', 'No']
    },

    painWithStraightening: {
      type: 'radio',
      label: 'Does it hurt when you straighten your knee?',
      options: ['Yes', 'No']
    },

    painWithStairs: {
      type: 'radio',
      label: 'Does it hurt when you go up or down stairs?',
      options: ['Yes', 'No']
    },

    painWithRunning: {
      type: 'radio',
      label: 'Does it hurt when you run?',
      options: ['Yes', 'No']
    },

    painAtNight: {
      type: 'radio',
      label: 'Does your thigh hurt at night?',
      options: ['Yes', 'No']
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Running',
        'Squatting',
        'Stairs',
        'Kicking',
        'Bending knee',
        'Standing up from chair',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Compression',
        'Elevation',
        'Pain medication',
        'Gentle movement',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling & Haematoma Section
    hasSwelling: {
      type: 'radio',
      label: 'Do you have any swelling in your thigh?',
      options: ['Yes', 'No']
    },

    swellingSize: {
      type: 'radio',
      label: 'How big is the swelling?',
      options: ['Small', 'Medium', 'Large'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    hasBruising: {
      type: 'radio',
      label: 'Do you have any bruising?',
      options: ['Yes', 'No']
    },

    bruisingTiming: {
      type: 'radio',
      label: 'How soon after the injury did the bruising appear?',
      options: [
        'Straight away',
        'Within 24 hours',
        'After more than a day',
        'No bruising'
      ]
    },

    hasHardLump: {
      type: 'radio',
      label: 'Can you feel a hard lump in your thigh?',
      options: ['Yes', 'No']
    },

    feelingOfFullness: {
      type: 'radio',
      label: 'Does your thigh feel full or very tight, like pressure is building up?',
      options: ['Yes', 'No']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'injury',
      title: '2. About Your Thigh Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['injuryDate', 'injuredSide', 'onsetType', 'injuryType', 'mainProblem', 'mainProblemOther', 'injuryDescription']
    },
    {
      id: 'mechanism',
      title: '3. How It Happened',
      component: 'GenericSection',
      enabled: true,
      fields: ['activityWhenInjured', 'directBlow', 'feltPop', 'immediatelyStopped', 'couldKneelOnIt', 'kneeFlexionRange']
    },
    {
      id: 'pain',
      title: '4. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painCharacter', 'painCharacterOther', 'painSeverity', 'painWithBending', 'painWithStraightening', 'painWithStairs', 'painWithRunning', 'painAtNight', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '5. Swelling & Bruising',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingSize', 'hasBruising', 'bruisingTiming', 'hasHardLump', 'feelingOfFullness']
    },
    {
      id: 'redFlags',
      title: '6. Important Safety Questions',
      component: 'RedFlagsSection',
      enabled: true,
      fields: [
        {
          id: 'severeIncreasingPain',
          type: 'radio',
          label: 'Is your thigh pain very bad and getting worse even with rest?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 8
        },
        {
          id: 'tenseSwollenThigh',
          type: 'radio',
          label: 'Does your thigh feel very tight, hard, and swollen?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 8
        },
        {
          id: 'numbnessTinglingLeg',
          type: 'radio',
          label: 'Do you have numbness or tingling in your lower leg or foot?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 6
        },
        {
          id: 'unableToWeightBear',
          type: 'radio',
          label: 'Are you unable to put weight on your leg?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'darkUrine',
          type: 'radio',
          label: 'Is your urine (pee) very dark, like cola or tea?',
          options: ['Yes', 'No'],
          urgent: true,
          weight: 6
        },
        {
          id: 'feverInfectedLook',
          type: 'radio',
          label: 'Do you have a fever or does the area look red and infected?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 5
        },
        {
          id: 'anticoagulants',
          type: 'radio',
          label: 'Do you take blood-thinning medication (e.g., warfarin, dabigatran)?',
          options: ['Yes', 'No'],
          urgent: false,
          weight: 3
        }
      ],
      alerts: {
        critical: {
          threshold: 8,
          message: 'URGENT: These symptoms need immediate medical attention. Please go to the Emergency Department.'
        },
        urgent: {
          threshold: 6,
          message: 'Please contact us promptly about these symptoms.'
        },
        concerning: {
          threshold: 5,
          message: 'We will review your answers carefully.'
        }
      }
    },
    {
      id: 'medical',
      title: '7. Your Health',
      component: 'MedicalHistorySection',
      enabled: true
    },
    {
      id: 'exerciseVitalSign',
      title: '8. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },
    {
      id: 'goals',
      title: '9. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly during activity': 'acute onset during activity',
      'After being hit': 'direct blow / contact injury',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Quadriceps contusion (Jackson Grade I)',
      'Quadriceps contusion (Jackson Grade II)',
      'Quadriceps contusion (Jackson Grade III)',
      'Quadriceps haematoma',
      'Rectus femoris strain',
      'Rectus femoris rupture',
      'Vastus strain',
      'Myositis ossificans',
      'Acute compartment syndrome (red flag)',
      'Femoral shaft stress fracture',
      'Referred hip/lumbar pathology'
    ]
  }
};

export default thighTemplate;
